import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";

export default function PasswordResetPage() {
    const [searchParams] = useSearchParams();
    const initialToken = searchParams.get("token") || "";
    const [mode, setMode] = useState(initialToken ? "confirm" : "request");
    const [email, setEmail] = useState("");
    const [token, setToken] = useState(initialToken);
    const [newPassword, setNewPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [devToken, setDevToken] = useState(null);

    const requestReset = async (ev) => {
        ev.preventDefault();
        if (!email) return toast.error("Inserisci l'email");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/password-reset/request", { email });
            toast.success(data?.message || "Richiesta inviata");
            if (data?.dev_token) {
                setDevToken(data.dev_token);
                setToken(data.dev_token);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setSubmitting(false);
        }
    };

    const confirmReset = async (ev) => {
        ev.preventDefault();
        if (!token) return toast.error("Token mancante");
        if (newPassword.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
        setSubmitting(true);
        try {
            await api.post("/auth/password-reset/confirm", { token, new_password: newPassword });
            toast.success("Password aggiornata. Ora puoi accedere.");
            setMode("done");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] p-6">
            <div className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#2563EB] mb-6" data-testid="back-to-login-link">
                    <ArrowLeft className="w-4 h-4" />
                    Torna al login
                </Link>

                <div className="bg-white border border-[#DCE3EE] rounded-2xl p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="font-bold text-[#172033]">IH PEC Guard</div>
                            <div className="text-xs text-[#64748B]">Recupero password</div>
                        </div>
                    </div>

                    {mode === "request" && (
                        <>
                            <h1 className="text-xl font-bold text-[#172033]">Password dimenticata</h1>
                            <p className="text-sm text-[#64748B] mt-2 mb-6">
                                Inserisci l'email del tuo account. Se esiste, riceverai le
                                istruzioni per reimpostare la password.
                            </p>
                            <form onSubmit={requestReset} className="space-y-4">
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="nome@azienda.it"
                                        data-testid="reset-email-input"
                                        className="mt-1.5 h-11"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="reset-request-btn"
                                    className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia richiesta"}
                                </Button>
                            </form>
                            {devToken && (
                                <div className="mt-6 p-3 rounded-lg bg-[#F5F7FB] border border-dashed border-[#DCE3EE] text-xs">
                                    <div className="font-semibold text-[#172033] mb-1">
                                        Token di sviluppo (solo ambiente dev):
                                    </div>
                                    <code className="break-all text-[#2563EB]">{devToken}</code>
                                    <Button
                                        variant="link"
                                        className="mt-2 h-auto p-0 text-xs"
                                        onClick={() => setMode("confirm")}
                                        data-testid="dev-use-token-btn"
                                    >
                                        Usa questo token per reimpostare
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {mode === "confirm" && (
                        <>
                            <h1 className="text-xl font-bold text-[#172033]">Reimposta password</h1>
                            <p className="text-sm text-[#64748B] mt-2 mb-6">
                                Inserisci il token ricevuto e la nuova password.
                            </p>
                            <form onSubmit={confirmReset} className="space-y-4">
                                <div>
                                    <Label htmlFor="token">Token</Label>
                                    <Input
                                        id="token"
                                        type="text"
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        data-testid="reset-token-input"
                                        className="mt-1.5 h-11 font-mono text-xs"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="newPassword">Nuova password</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        data-testid="reset-new-password-input"
                                        className="mt-1.5 h-11"
                                        placeholder="Almeno 8 caratteri"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="reset-confirm-btn"
                                    className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reimposta password"}
                                </Button>
                            </form>
                        </>
                    )}

                    {mode === "done" && (
                        <div className="text-center py-6">
                            <div className="w-14 h-14 rounded-full bg-[#16A34A]/10 flex items-center justify-center mx-auto mb-4">
                                <ShieldCheck className="w-7 h-7 text-[#16A34A]" />
                            </div>
                            <h1 className="text-xl font-bold text-[#172033]">Password aggiornata</h1>
                            <p className="text-sm text-[#64748B] mt-2 mb-6">
                                Ora puoi accedere con la nuova password.
                            </p>
                            <Link to="/login" data-testid="reset-done-login-link">
                                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Vai al login</Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
